import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { Button, ButtonStyle, Checkbox, Form, FormField, Spinner } from "@prequel/shared-components";

import { useAllProducts } from "@/api/queries/products";
import { useCreateRecipient, useRecipient, useUpdateRecipient } from "@/api/queries/recipients";
import { computeRecipientChangedFields, Recipient } from "@/types";

const RecipientForm = () => {
  const navigate = useNavigate();
  const { recipientID } = useParams<{ recipientID: string }>();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { data: products = [] } = useAllProducts();
  const recipientQueryResult = useRecipient(recipientID);
  const createRecipientMutation = useCreateRecipient();
  const updateRecipientMutation = useUpdateRecipient();

  const recipientToEdit = recipientQueryResult.data;
  const isEditing = !!recipientToEdit;
  const [recipient, setRecipient] = useState<Recipient>({
    name: "",
    id_in_provider_system: "",
    products: [],
  });
  const [availableProducts, setAvailableProducts] = useState<string[]>();
  const [productsError, setProductsError] = useState<string>();

  const isProcessing = createRecipientMutation.isPending || updateRecipientMutation.isPending;

  useEffect(() => {
    if (recipientToEdit) {
      setRecipient(recipientToEdit);
    }
  }, [recipientToEdit]);

  useEffect(() => {
    if (products) {
      const opts = products.map(({ product_name }) => product_name);
      setAvailableProducts(opts);
    }
  }, [products]);

  const setField = (key: keyof Recipient, value: string | string[]) => {
    setRecipient((oldRecipient: Recipient) => ({
      ...oldRecipient,
      [key]: value,
    }));
  };

  const updateProducts = (isEnabled: boolean, productName: string) => {
    const updatedProducts = isEnabled
      ? [...recipient.products, productName]
      : recipient.products.filter((p) => p !== productName);
    setProductsError(undefined);
    setField("products", updatedProducts);
  };

  const onSubmit = () => {
    if (availableProducts && recipient.products.length === 0) {
      setProductsError("Must select at least one product");
      return;
    }

    if (isEditing && recipientToEdit) {
      updateRecipientMutation.mutate({
        recipientID: recipientToEdit.id,
        fields: computeRecipientChangedFields(recipientToEdit, recipient),
        redirectPath: `/recipients/${recipientToEdit.id}`,
      });
    } else {
      createRecipientMutation.mutate({
        recipient,
        redirectPath: "/recipients",
      });
    }
  };

  return (
    <Form className="space-y-4" onSubmit={onSubmit} submitButtonRef={buttonRef}>
      <FormField
        label="Name"
        id="name"
        type="text"
        subtext="Descriptive name for this Recipient. This will only be visible internally and is only used as a reference."
        value={recipient.name}
        onChangeHandler={(value: string) => setField("name", value)}
        disabled={isProcessing}
        required
      />
      <FormField
        label="ID in Provider System"
        id="id_in_provider_system"
        type="text"
        subtext="Customer ID by which the source data will be filtered. In other words, this is the ID of the target customer in your source database. An engineer or database administrator can provide the unique value for each recipient."
        value={recipient.id_in_provider_system}
        onChangeHandler={(value: string) => setField("id_in_provider_system", value)}
        required
        disabled={isEditing || isProcessing}
      />
      {availableProducts && (
        <>
          <label className="block text-sm font-medium text-gray-700">
            Select what products the recipient will receive
          </label>
          {availableProducts.map((p) => (
            <Checkbox
              key={p}
              id={p}
              label={p}
              checked={recipient.products.includes(p)}
              setChecked={(isChecked: boolean) => updateProducts(isChecked, p)}
              disabled={isProcessing}
            />
          ))}
          {productsError && <p className="mt-1 text-xs font-medium text-red-600">{productsError}</p>}
        </>
      )}
      <div className="mt-8 flex justify-end">
        {isEditing && (
          <Button
            className="mr-3"
            type={ButtonStyle.TERTIARY}
            disabled={isProcessing}
            onClick={() => navigate(-1)}
            text="Cancel"
          />
        )}
        <Button
          ref={buttonRef}
          type={ButtonStyle.PRIMARY}
          disabled={isProcessing}
          text={
            isProcessing ? (
              <div className="flex">
                <Spinner.Inline className="mr-3 text-white" /> Saving Recipient...
              </div>
            ) : (
              "Save Recipient"
            )
          }
          submit
        />
      </div>
    </Form>
  );
};

export default RecipientForm;
